import { InvoiceStatus } from "src/constants/invoice";
import type { Invoice } from "src/types";

export type InvoiceViewKey = "all" | "open" | "overdue" | "unsent" | "paid" | "writtenOff";

export function isActiveInvoice(invoice: Invoice): boolean {
    return invoice.status !== InvoiceStatus.DELETED;
}

export function matchesInvoiceView(invoice: Invoice, viewKey: InvoiceViewKey): boolean {
    if (!isActiveInvoice(invoice)) return false;
    switch (viewKey) {
        case "all":
            return true;
        case "open":
            return (
                !!invoice.sentDate &&
                invoice.status !== InvoiceStatus.PAID &&
                invoice.status !== InvoiceStatus.WRITTEN_OFF
            );
        case "overdue":
            return invoice.status === InvoiceStatus.OVERDUE ||
                   (invoice.status === InvoiceStatus.OPEN && (invoice.dunning ?? 0) >= 1);
        case "unsent":
            return !invoice.sentDate &&
                   invoice.status !== InvoiceStatus.PAID &&
                   invoice.status !== InvoiceStatus.WRITTEN_OFF;
        case "paid":
            return invoice.status === InvoiceStatus.PAID;
        case "writtenOff":
            return invoice.status === InvoiceStatus.WRITTEN_OFF;
        default:
            return true;
    }
}

export function computeInvoiceViewCounts(invoices: Invoice[]) {
    const active = invoices.filter(isActiveInvoice);
    return {
        all: active.length,
        open: active.filter((i) => matchesInvoiceView(i, "open")).length,
        overdue: active.filter((i) => matchesInvoiceView(i, "overdue")).length,
        unsent: active.filter((i) => matchesInvoiceView(i, "unsent")).length,
        paid: active.filter((i) => matchesInvoiceView(i, "paid")).length,
        writtenOff: active.filter((i) => matchesInvoiceView(i, "writtenOff")).length,
    };
}

export function invoiceDisplayName(invoice: Invoice): string {
    const billed = `${invoice.patient?.firstName ?? ""} ${invoice.patient?.lastName ?? ""}`.trim();
    const account = `${invoice.recipient?.firstName ?? ""} ${invoice.recipient?.lastName ?? ""}`.trim();
    return billed || account || "—";
}

export function formatShortDate(dateStr?: string): string {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}
